import RPC from '@exodus/json-rpc';
import {
  createBtcDepsFactory,
  registerRPCHandlers,
} from '@exodus/web3-bitcoin/rpc';

import createLogger from '@/utils/logger';
import { createAppDeps, createSignerDeps, createWalletDeps } from './deps.js';
import type { Web3Dependencies } from '../types.js';
import { createTransport } from '../utils/transport.js';

const ASSET_NAME = 'bitcoin';

type BitcoinDependecies = Parameters<typeof registerRPCHandlers>[0]['deps'];

const createBitcoinDeps = (exodus: Web3Dependencies): BitcoinDependecies => {
  const createBtcDeps = createBtcDepsFactory();

  const broadcastTx = async (signedTx: string) => {
    const walletAccount = await exodus.walletAccounts.getActive();

    return exodus.transactions.broadcast({
      assetName: ASSET_NAME,
      signedTx,
      walletAccount,
    });
  };

  const getActiveWalletAccountData = async () => {
    const walletAccounts = await exodus.walletAccounts.getEnabled();
    const activeWalletAccount = await exodus.walletAccounts.getActive();
    return walletAccounts[activeWalletAccount];
  };

  const getAddress = async (assetName: string, purpose?: number) => {
    const walletAccount = await getActiveWalletAccountData();
    const { address } = await exodus.addressProvider.getReceiveAddress({
      assetName,
      walletAccount,
      purpose,
    });
    return address;
  };

  const getPaymentAddressPurpose = async ({ assetName, walletAccount }) =>
    exodus.addressProvider.getDefaultPurpose({ assetName, walletAccount });

  const getPublicKey = async (assetName: string, purpose?: number) => {
    const { baseAsset } = exodus.assetsModule.getAsset(assetName);
    const keyIdentifier = baseAsset.api.getKeyIdentifier({
      purpose,
      accountIndex: 0,
      chainIndex: 0,
      addressIndex: 0,
    });
    return exodus.publicKeyProvider.getPublicKey({ keyIdentifier });
  };

  const { transactionSigner, messageSigner } = createSignerDeps({
    assetName: ASSET_NAME,
    transactionSigner: exodus.transactionSigner,
    messageSigner: exodus.messageSigner,
    analytics: exodus.analytics,
  });

  const btcDeps = createBtcDeps({
    assetName: ASSET_NAME,
    broadcastTx,
    getActiveWalletAccountData,
    getAddress,
    getPaymentAddressPurpose,
    getPublicKey,
    transactionSigner,
    messageSigner,
    logger: createLogger('bitcoin'),
  });

  return {
    bitcoin: btcDeps,
    app: createAppDeps(exodus),
    wallet: createWalletDeps(exodus),
  };
};

export const createBitcoinRequestHandler = (exodus: Web3Dependencies) => {
  const transport = createTransport();

  const deps = createBitcoinDeps(exodus);

  registerRPCHandlers({ deps, transport, RPC });

  return (request: unknown) => {
    transport.emit('data', request);
  };
};
